import { combineEpics, type Epic } from 'redux-observable';
import {
  filter,
  from,
  fromEvent,
  mergeMap,
  map,
  catchError,
  of,
  merge,
  ignoreElements,
  withLatestFrom,
  Observable,
  EMPTY,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import i18n from 'i18next';
import { initPusher, disconnectPusher } from '@/core/services/pusher';
import {
  getNotifications,
  getUnreadCount,
  markNotificationRead,
  markNotificationUnread,
  markAllNotificationsRead,
  type NotificationItem,
} from '@/api/istaff/notifications';
import { setAuth, setUser, clearAuth } from '@/slices/auth/actions';
import { selectAccessToken } from '@/slices/auth/selectors';
import type { RootState } from '@/state/store';
import {
  loadNotifications,
  loadMoreNotifications,
  syncUnreadCount,
  markRead,
  markUnread,
  markAllRead,
  setLoading,
  setNotificationsPage,
  setUnreadCount,
  markItemRead,
  markItemUnread,
  setAllRead,
  prependNotification,
} from '../actions';
import { selectNotificationsPage } from '../selectors';

const loadNotificationsEpic: Epic = (action$) =>
  action$.pipe(
    filter(loadNotifications.match),
    mergeMap(({ payload }) =>
      merge(
        of(setLoading(true)),
        from(getNotifications(payload?.page ?? 1)).pipe(
          map((res) => setNotificationsPage(res)),
          catchError(() => of(setLoading(false))),
        ),
      ),
    ),
  );

const loadMoreNotificationsEpic: Epic = (action$, state$) =>
  action$.pipe(
    filter(loadMoreNotifications.match),
    withLatestFrom(state$ as Observable<RootState>),
    mergeMap(([, state]) => {
      const currentPage = selectNotificationsPage(state);
      return merge(
        of(setLoading(true)),
        from(getNotifications(currentPage + 1)).pipe(
          map((res) => setNotificationsPage(res)),
          catchError(() => of(setLoading(false))),
        ),
      );
    }),
  );

const languageChangeEpic: Epic = (action$, state$) =>
  fromEvent(i18n, 'languageChanged').pipe(
    withLatestFrom(state$ as Observable<RootState>),
    filter(([, state]) => !!selectAccessToken(state)),
    map(() => loadNotifications({ page: 1 })),
  );

const syncUnreadCountEpic: Epic = (action$) =>
  action$.pipe(
    filter(syncUnreadCount.match),
    mergeMap(() =>
      from(getUnreadCount()).pipe(
        map((res) => setUnreadCount(res.unread_count)),
        catchError(() => of(setUnreadCount(0))),
      ),
    ),
  );

const optimisticMarkEpic = (
  action: typeof markRead | typeof markUnread,
  apiCall: (id: string) => Promise<{ message: string }>,
  optimisticAction: (
    id: string,
  ) => ReturnType<typeof markItemRead | typeof markItemUnread>,
  rollbackAction: (
    id: string,
  ) => ReturnType<typeof markItemRead | typeof markItemUnread>,
): Epic =>
  ((action$) =>
    action$.pipe(
      filter(action.match),
      mergeMap(({ payload: id }) =>
        merge(
          of(optimisticAction(id)),
          from(apiCall(id)).pipe(
            ignoreElements(),
            catchError(() => of(rollbackAction(id))),
          ),
        ),
      ),
    )) as Epic;

const markReadEpic = optimisticMarkEpic(
  markRead,
  markNotificationRead,
  markItemRead,
  markItemUnread,
);

const markUnreadEpic = optimisticMarkEpic(
  markUnread,
  markNotificationUnread,
  markItemUnread,
  markItemRead,
);

const markAllReadEpic: Epic = (action$) =>
  action$.pipe(
    filter(markAllRead.match),
    mergeMap(() =>
      merge(
        of(setAllRead()),
        from(markAllNotificationsRead()).pipe(
          ignoreElements(),
          catchError(() => of(syncUnreadCount())),
        ),
      ),
    ),
  );

const NOTIFICATION_EVENT =
  'Illuminate\\Notifications\\Events\\BroadcastNotificationCreated';

const pusherEpic: Epic = (action$, state$) =>
  merge(
    action$.pipe(
      filter(setAuth.match),
      map(({ payload }) => ({
        userId: payload.user.id,
        token: payload.accessToken,
      })),
    ),
    action$.pipe(
      filter(setUser.match),
      withLatestFrom(state$ as Observable<RootState>),
      map(([{ payload }, state]) => ({
        userId: payload.id,
        token: selectAccessToken(state),
      })),
    ),
  ).pipe(
    switchMap(({ token, userId }) => {
      if (!token) return EMPTY;
      const pusher = initPusher(token);
      const channelName = `private-users.${userId}.notifications`;
      const channel = pusher.subscribe(channelName);
      return new Observable<ReturnType<typeof prependNotification>>(
        (observer) => {
          channel.bind(NOTIFICATION_EVENT, (data: NotificationItem) => {
            observer.next(prependNotification(data));
          });
          // Only tear down this feature's own channel — the Pusher
          // connection is shared with other features (e.g. admin items).
          return () => {
            channel.unbind_all();
            pusher.unsubscribe(channelName);
          };
        },
      ).pipe(takeUntil(action$.pipe(filter(clearAuth.match))));
    }),
  );

// Fully closes the shared Pusher connection once, on logout.
const pusherDisconnectEpic: Epic = (action$) =>
  action$.pipe(
    filter(clearAuth.match),
    tap(() => disconnectPusher()),
    ignoreElements(),
  );

export default combineEpics(
  loadNotificationsEpic,
  loadMoreNotificationsEpic,
  languageChangeEpic,
  syncUnreadCountEpic,
  markReadEpic,
  markUnreadEpic,
  markAllReadEpic,
  pusherEpic,
  pusherDisconnectEpic,
);
